import { useTranslation } from 'react-i18next';
import { Gift } from 'lucide-react';
import type { WalletCredit } from '@/lib/api';
import { formatMoneyExplicit } from '@/lib/money';
import styles from './WalletPage.module.css';

interface Props {
  credits: readonly WalletCredit[] | undefined;
}

/**
 * The driver's make-good credits (Warren, 2026-10-28): one card each, with
 * what is left of it, the date it expires and its one-line reason (stored
 * English text, shown as written). The credit is already inside the wallet
 * balance above in its currency; the note says so, and that it is used first
 * when the wallet pays. Nothing renders without a credit.
 */
export function WalletCreditList({ credits }: Props) {
  const { t, i18n } = useTranslation('wallet');
  const list = (credits ?? []).filter((c) => c.remainingCents > 0);
  if (!list.length) return null;
  return (
    <section className={styles.creditSection} aria-label={t('credits.title')}>
      <h2 className={styles.cardTitle}>{t('credits.title')}</h2>
      {list.map((c) => (
        <div key={c.id} className={styles.creditCard} data-make-good-credit={c.id} data-credit-currency={c.currency}>
          <span className={styles.creditIcon} aria-hidden><Gift size={18} /></span>
          <div className={styles.balanceText}>
            <span className={styles.creditAmount}>
              {t('credits.amount', { amount: formatMoneyExplicit(c.remainingCents, c.currency, i18n.language) })}
            </span>
            <span className={styles.creditReason}>{c.reason}</span>
            <span className={styles.balanceMeta}>
              {t('credits.expires', { date: creditDate(c.expiresAt, i18n.language) })}
              {' '}
              {t('credits.included', { currency: c.currency })}
            </span>
          </div>
        </div>
      ))}
    </section>
  );
}

/** "January 26, 2027" in the driver's language. */
export function creditDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  try {
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}
